/** The lines of a room's Everyone or a circle: days, what is new, hover actions, and the menu that keeps it calm. */
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router";
import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { TalkLiveChatBlock, TalkMessage } from "../../shared/talk";
import { Button } from "../components/ui/Button";
import { Face } from "../components/ui/Face";
import { Icon, type IconName } from "../components/ui/Icon";
import { Sheet } from "../components/ui/Sheet";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { useOpenDirect } from "./handoff";
import { CallCard, Line } from "./parts";
import { dateWords, dayLabel, firstName, typingWords } from "./when";

interface LineAction {
  icon: IconName;
  label: string;
  bad?: boolean;
  run: () => void;
}

/** How long a line found through Show in the chat or a search stays lit. */
const FLASH_MS = 2400;

/**
 * A chat opens at its newest line and stays there while the person is at the end of it. An address that names a
 * line (`?at=<id>`, from Files and search) opens on that line instead and lights it.
 */
export function useStick(ref: RefObject<HTMLElement | null>, count: number, place: string) {
  const near = useRef(true);
  const was = useRef(place);
  const at = useSearchParams()[0].get("at");
  const shown = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (was.current !== place) {
      was.current = place;
      near.current = true;
    }
    const el = ref.current;
    const key = `${place}:${at}`;
    const target = at && shown.current !== key ? el?.querySelector<HTMLElement>(`[data-line="${CSS.escape(at)}"]`) : null;
    if (target) {
      shown.current = key;
      near.current = false;
      target.scrollIntoView({ block: "center" });
      target.classList.add("flash");
      window.setTimeout(() => target.classList.remove("flash"), FLASH_MS);
    } else if (el && near.current) el.scrollTop = el.scrollHeight;
  }, [ref, count, place, at]);
  return useCallback((e: { currentTarget: HTMLElement }) => {
    const el = e.currentTarget;
    near.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);
}

/** What Maya can do to a line, and the reply she is writing. `webinarId` is set where a room's holds apply. */
export function useLineTools({ webinarId, patch, reload }: { webinarId: string | null; patch: (change: (m: TalkMessage) => TalkMessage, id: string) => void; reload: () => void }) {
  const toast = useToast();
  const openDirect = useOpenDirect();
  const [open, setOpen] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<TalkMessage | null>(null);
  const [removing, setRemoving] = useState<TalkMessage | null>(null);

  const fail = () => toast({ text: "We couldn't save that. Try again.", icon: "info" });

  const heart = (m: TalkMessage) => {
    patch((x) => ({ ...x, hearted: !x.hearted, hearts: x.hearts + (x.hearted ? -1 : 1) }), m.id);
    api.talk.heart(m.id, !m.hearted).catch(() => {
      patch((x) => ({ ...x, hearted: m.hearted, hearts: m.hearts }), m.id);
      fail();
    });
  };

  const actions = (m: TalkMessage): LineAction[] => {
    const close = () => setOpen(null);
    if (m.who.kind === "host") {
      return [
        {
          icon: "pin",
          label: m.pinned ? "Unpin it" : "Pin it",
          run: () => {
            close();
            api.talk.pin(m.id, !m.pinned).then(reload, fail);
          },
        },
      ];
    }
    const who = m.who;
    const first = firstName(who.name);
    const list: LineAction[] = [
      {
        icon: "chat",
        label: "Talk privately",
        run: () => {
          close();
          openDirect(who.registrantId).catch(fail);
        },
      },
    ];
    if (webinarId) {
      list.push({
        icon: "clock",
        label: `Pause ${first}'s posts for a day`,
        run: () => {
          close();
          api.talk.hold(webinarId, { registrantId: who.registrantId, kind: "paused" }).then(() => toast(`${first} can read but not post for a day`), fail);
        },
      });
    }
    if (!m.hidden) {
      list.push({
        icon: "eye",
        label: "Hide this message",
        run: () => {
          close();
          patch((x) => ({ ...x, hidden: true }), m.id);
          api.talk.hide(m.id).then(() => toast("The message is hidden from the room"), () => {
            patch((x) => ({ ...x, hidden: false }), m.id);
            fail();
          });
        },
      });
    }
    if (webinarId) {
      list.push({
        icon: "x",
        label: "Remove from the room",
        bad: true,
        run: () => {
          close();
          setRemoving(m);
        },
      });
    }
    return list;
  };

  const confirmRemove = () => {
    const m = removing;
    if (!m || m.who.kind !== "member" || !webinarId) return;
    const first = firstName(m.who.name);
    setRemoving(null);
    api.talk.hold(webinarId, { registrantId: m.who.registrantId, kind: "removed" }).then(() => {
      toast(`${first} is out of the room`);
      reload();
    }, fail);
  };

  return { open, setOpen, replyTo, setReplyTo, removing, setRemoving, heart, actions, confirmRemove };
}

export type LineTools = ReturnType<typeof useLineTools>;

/** Asks before a person is taken out of the room. */
export function RemoveSheet({ tools }: { tools: LineTools }) {
  const m = tools.removing;
  const name = m?.who.name ?? "";
  return (
    <Sheet
      open={!!m}
      onClose={() => tools.setRemoving(null)}
      title={`Remove ${name} from the room?`}
      footer={
        <>
          <Button onClick={() => tools.setRemoving(null)}>Not now</Button>
          <Button variant="p" onClick={tools.confirmRemove}>
            Remove {firstName(name)}
          </Button>
        </>
      }
    >
      <div className="sheet-b">
        <p>{firstName(name)} will not see this room or post in it any more.</p>
      </div>
    </Sheet>
  );
}

/** The menu on a line: under the "..." on a computer and an iPad, a sheet from below on a phone. */
function Menu({ m, tools, phone, host }: { m: TalkMessage; tools: LineTools; phone: boolean; host: { name: string; photo: string | null } }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && tools.setOpen(null);
    const onDown = (e: MouseEvent) => !phone && !(e.target as HTMLElement).closest(".mod,.ln-acts") && tools.setOpen(null);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [tools, phone]);
  const acts = tools.actions(m);
  const who = m.who.kind === "host" ? host : { name: m.who.name, photo: m.who.photoUrl };
  if (!phone) {
    return (
      <div className="mod" role="menu" aria-label={who.name}>
        <p className="mod-h">
          <Face name={who.name} photo={who.photo} size={28} />
          <b>{who.name}</b>
        </p>
        {acts.map((a) => (
          <button key={a.label} type="button" role="menuitem" className={a.bad ? "bad" : undefined} onClick={a.run}>
            <Icon name={a.icon} /> {a.label}
          </button>
        ))}
      </div>
    );
  }
  // Why a portal: the sheet covers the whole screen, which a line's own box would cut to its size.
  return createPortal(
    <div className="hp-veil" onClick={(e) => (e.stopPropagation(), tools.setOpen(null))}>
      <div className="hp-acts" role="menu" aria-label={who.name} onClick={(e) => e.stopPropagation()}>
        <div className="hp-acts-card">
          <p className="hp-acts-h">
            <Face name={who.name} photo={who.photo} size={32} />
            <span>
              <b>{who.name}</b>
              <small>{m.body}</small>
            </span>
          </p>
          <button type="button" role="menuitem" onClick={() => (tools.setOpen(null), tools.heart(m))}>
            <Icon name="heart" />
            {m.hearted ? "Take the heart back" : "Heart"}
          </button>
          <button type="button" role="menuitem" onClick={() => (tools.setOpen(null), tools.setReplyTo(m))}>
            <Icon name="chat" />
            Reply
          </button>
          {acts.map((a) => (
            <button key={a.label} type="button" role="menuitem" className={a.bad ? "bad" : undefined} onClick={a.run}>
              <Icon name={a.icon} />
              {a.label}
            </button>
          ))}
        </div>
        <button type="button" className="hp-acts-x" onClick={() => tools.setOpen(null)}>
          Cancel
        </button>
      </div>
    </div>,
    document.querySelector(".hp") ?? document.body,
  );
}

type Item = { kind: "sep"; label: string } | { kind: "new" } | { kind: "live"; block: TalkLiveChatBlock } | { kind: "msg"; m: TalkMessage };

/** The chat's rows in time order: day headings, the live chats of past lives, the "new" line, the lines. */
export function timeline(messages: TalkMessage[], liveChats: TalkLiveChatBlock[], seenAt: number | null, now = Date.now()): Item[] {
  const lines = messages.toSorted((a, b) => a.createdAt - b.createdAt);
  const lives = liveChats.toSorted((a, b) => a.startsAt - b.startsAt);
  const firstNew = seenAt === null ? -1 : lines.findIndex((m) => m.createdAt > seenAt && m.who.kind !== "host");
  const out: Item[] = [];
  let day = "";
  const put = (at: number) => {
    const label = dayLabel(at, now);
    if (label !== day) {
      day = label;
      out.push({ kind: "sep", label });
    }
  };
  let l = 0;
  lines.forEach((m, i) => {
    while (lives[l] && (lives[l] as TalkLiveChatBlock).startsAt <= m.createdAt) {
      const block = lives[l++] as TalkLiveChatBlock;
      put(block.startsAt);
      out.push({ kind: "live", block });
    }
    put(m.createdAt);
    if (i === firstNew) out.push({ kind: "new" });
    out.push({ kind: "msg", m });
  });
  for (; lives[l]; l++) {
    const block = lives[l] as TalkLiveChatBlock;
    put(block.startsAt);
    out.push({ kind: "live", block });
  }
  return out;
}

export function ChatList({
  messages,
  liveChats = [],
  seenAt,
  tools,
  phone,
  host,
  members,
  typing,
  lead,
  onWatch,
  className,
  scroll,
  onScroll,
}: {
  messages: TalkMessage[];
  liveChats?: TalkLiveChatBlock[];
  seenAt: number | null;
  tools: LineTools;
  phone: boolean;
  host: { name: string; photo: string | null };
  members: number;
  typing: string[];
  lead?: React.ReactNode;
  onWatch: (m: TalkMessage) => void;
  className: string;
  scroll: RefObject<HTMLDivElement | null>;
  onScroll: (e: { currentTarget: HTMLElement }) => void;
}) {
  const items = timeline(messages, liveChats, seenAt);
  return (
    <div className={className} ref={scroll} onScroll={onScroll}>
      {lead}
      {items.map((it, i) => {
        if (it.kind === "sep") return <p key={`s${i}`} className="tl-sep">{it.label}</p>;
        if (it.kind === "new")
          return (
            <p key="new" className="newline">
              <span>New since you were here</span>
            </p>
          );
        if (it.kind === "live")
          return (
            <p key={`l${it.block.liveSessionId}`} className="sysline">
              <Icon name="live" /> The live chat, {dateWords(it.block.startsAt)} · {it.block.count} {it.block.count === 1 ? "line" : "lines"}
            </p>
          );
        const m = it.m;
        if (m.call) return <CallCard key={m.id} m={m} members={members} onWatch={() => onWatch(m)} />;
        const own = m.who.kind === "host";
        const open = tools.open === m.id;
        return (
          <Line
            key={m.id}
            m={m}
            size={phone ? 32 : 38}
            host={host}
            menuOpen={open}
            onHeart={phone ? undefined : () => tools.heart(m)}
            onReply={phone || own ? undefined : () => tools.setReplyTo(m)}
            onMenu={phone ? undefined : () => tools.setOpen(open ? null : m.id)}
            menu={open ? <Menu m={m} tools={tools} phone={phone} host={host} /> : undefined}
            onTap={phone ? () => tools.setOpen(m.id) : undefined}
          />
        );
      })}
      {typing.length ? (
        <p className="typing">
          <span>
            <i />
            <i />
            <i />
          </span>
          {typingWords(typing)}
        </p>
      ) : null}
    </div>
  );
}
